import './FlowHeaderV2.scss';

import React, { useCallback } from 'react';

import { FlightButton, FlightTextInput, FlightTooltip } from '@flybits/design-system';

import { useThunkDispatch as useDispatch, useAppSelector as useSelector } from 'hooks/reduxHooks';
import { isJourneyDirty } from 'store/journey/journey.selector';
import { ActionTypes } from 'store/actionTypes';
import { JourneyState } from 'store/journey/journey.type';
import UnsavedPrompt from 'components/Shared/UnsavedPrompt/UnsavedPrompt';

import { UseJourneyActions } from '../hooks/useJourneyActions';
import { ExperienceNameValidation } from './useExperienceNameValidation';

const MAIN_CLASS = 'flow-header-v2';
const CLASSES = {
  LEFT: `${MAIN_CLASS}__left`,
  RIGHT: `${MAIN_CLASS}__right`,
  NAME_INPUT: `${MAIN_CLASS}__name-input`,
};

type FlowHeaderV2Props = {
  journeyInstance: JourneyState;
  actions: Pick<UseJourneyActions, 'save' | 'exit' | 'isBusy' | 'ConfirmModalSlot' | 'LaunchConfirmModalSlot'>;
  nameValidation: ExperienceNameValidation;
};

/**
 * Flow v2 header (per Figma 5906-4750): the experience-name input on the left and **Save** (filled)
 * + **Exit** (outlined) pills on the right — no launch / deactivate / schedule / status tag, since
 * those move onto the floating {@link JourneyLaunchPopover}. The header mounts the action layer's
 * confirm-modal slots and the unsaved-changes prompt once.
 */
function FlowHeaderV2({ journeyInstance, actions, nameValidation }: FlowHeaderV2Props): JSX.Element {
  const dispatch = useDispatch();
  const isDirty = useSelector((state) => isJourneyDirty(state));
  const { experienceName, experienceNameError, onChangeName } = nameValidation;
  const { save, exit, isBusy, ConfirmModalSlot, LaunchConfirmModalSlot } = actions;

  const clearJourney = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_JOURNEY });
  }, [dispatch]);

  const isSaveDisabled = isBusy || !!experienceNameError.length;

  return (
    <>
      <div className={MAIN_CLASS}>
        <div className={CLASSES.LEFT}>
          <FlightTooltip
            direction="bottom"
            description={experienceName || journeyInstance.templateName || '<Please enter the experience name>'}
          >
            <FlightTextInput
              theme="lite"
              className={CLASSES.NAME_INPUT}
              value={experienceName}
              width="370px"
              label="Name this experience*"
              placeholderText={journeyInstance.templateName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeName(e.target.value || '')}
              trailingIconTooltipDescription={experienceNameError}
              hasError={!!experienceNameError.length || !experienceName}
            />
          </FlightTooltip>
        </div>
        <div className={CLASSES.RIGHT}>
          <FlightTooltip
            isEnabled={!!experienceNameError.length}
            description="Rename your experience to save"
            direction="bottom"
          >
            <FlightButton label="Save" onClick={save} disabled={isSaveDisabled} loading={isBusy} />
          </FlightTooltip>
          <FlightButton theme="secondary" label="Exit" onClick={exit} />
        </div>
      </div>
      {ConfirmModalSlot()}
      {LaunchConfirmModalSlot()}
      <UnsavedPrompt
        when={isDirty}
        unblockPaths={['/overview', '/flow-builder', '/touchpoint']}
        dialogProps={{ description: 'This will undo all the information you have entered since you started.' }}
        callback={clearJourney}
      />
    </>
  );
}

export default FlowHeaderV2;
